import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AnyBlock } from "@juro/shared";
import { Modal } from "../Modal.js";
import { Button } from "../ui/Button.js";
import { toast } from "../Toast.js";
import { tenantProfessionsApi, type ProfessionRule } from "../../api/tenantProfessionsApi.js";
import { useDocTypeProfession } from "../../hooks/useDocTypeProfession.js";

/** 専用の追加ボタンが既にある field_key はピッカーの一覧から除外する */
const BUILTIN_FIELD_KEYS = new Set([
  "holder_signature",
  "invoice_registration_number",
  "registration_number",
  "business_label",
]);

function newBlockSuffix(): string {
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
}

function buildKvBlock(rule: Pick<ProfessionRule, "label" | "field_key">): AnyBlock {
  return {
    id: `blk_issuer_custom_${newBlockSuffix()}`,
    label: rule.label,
    type: "kv_block",
    orientation: "horizontal",
    items: [
      {
        label: rule.label,
        value: `{{issuer.rules.${rule.field_key}}}`,
        data_key: `issuer.rules.${rule.field_key}`,
        input_type: "text",
      },
    ],
  };
}

function nextAutoFieldKey(existing: ProfessionRule[]): string {
  const used = new Set(existing.map((rule) => rule.field_key));
  let n = 1;
  while (used.has(`item_${n}`)) n += 1;
  return `item_${n}`;
}

/**
 * 「+ カスタム項目」ボタン用のピッカー。
 * 業務マスタ（tenant_professions のルール）から既存の項目を選ぶか、
 * その場で新しい項目を追加してテンプレートへ kv_block として挿入する。
 */
export function CustomItemPicker({
  docType,
  onPick,
  onClose,
}: {
  docType: string;
  onPick: (block: AnyBlock) => void;
  onClose: () => void;
}) {
  const { profession: professionKey } = useDocTypeProfession(docType);
  const queryClient = useQueryClient();
  const professionsQuery = useQuery({
    queryKey: ["tenant-professions"],
    queryFn: () => tenantProfessionsApi.list(),
  });

  const [newLabel, setNewLabel] = useState("");
  const [newValue, setNewValue] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const profession = professionsQuery.data?.data.find((entry) => entry.profession === professionKey) ?? null;
  const pickableRules = (profession?.rules ?? []).filter((rule) => !BUILTIN_FIELD_KEYS.has(rule.field_key));

  const createMut = useMutation({
    mutationFn: async () => {
      if (!profession) throw new Error("この書類種別には発行者情報がありません");
      const fieldKey = nextAutoFieldKey(profession.rules);
      return tenantProfessionsApi.createRule(profession.profession, {
        profession: profession.profession,
        field_key: fieldKey,
        label: newLabel.trim(),
        value: newValue.trim() || null,
        applies_to_doc_types: null,
        display_position: "header_right",
        display_format: "label_colon_value",
        display_order: 50,
      });
    },
    onSuccess: (rule) => {
      queryClient.invalidateQueries({ queryKey: ["tenant-professions"] });
      toast("項目を追加しました");
      onPick(buildKvBlock(rule));
      onClose();
    },
    onError: (error) => toast(error instanceof Error ? error.message : String(error), "error"),
  });

  function handleCreate() {
    setFormError(null);
    if (!newLabel.trim()) {
      setFormError("ラベルを入力してください");
      return;
    }
    createMut.mutate();
  }

  return (
    <Modal title="カスタム項目を追加" onClose={onClose} width={520}>
      {professionsQuery.isLoading ? (
        <div style={{ padding: 20, color: "#64748b" }}>読み込み中...</div>
      ) : !profession ? (
        <div style={{ padding: 20, color: "#64748b" }}>この書類種別には発行者情報がありません。</div>
      ) : (
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800, marginBottom: 8 }}>登録済みの項目から選ぶ</div>
            {pickableRules.length === 0 ? (
              <div style={{ fontSize: 13, color: "#94a3b8" }}>登録済みの項目はありません。</div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {pickableRules.map((rule) => (
                  <div
                    key={rule.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      padding: "8px 10px",
                      border: "1px solid #e2e8f0",
                      borderRadius: 8,
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: "#0f172a", fontWeight: 700 }}>{rule.label}</div>
                      <div style={{ fontSize: 12, color: rule.value ? "#334155" : "#94a3b8", marginTop: 2 }}>
                        {rule.value || "未入力"}
                      </div>
                    </div>
                    <Button
                      variant="primary"
                      style={{ flexShrink: 0 }}
                      onClick={() => {
                        onPick(buildKvBlock(rule));
                        onClose();
                      }}
                    >
                      追加
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid #e2e8f0", paddingTop: 14 }}>
            <div style={{ fontSize: 12, color: "#64748b", fontWeight: 800, marginBottom: 8 }}>新しい項目を作る</div>
            <div style={{ display: "grid", gap: 8 }}>
              <input
                value={newLabel}
                placeholder="ラベル（例: 事務所印）"
                onChange={(event) => setNewLabel(event.target.value)}
                style={{ height: 34, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }}
              />
              <input
                value={newValue}
                placeholder="値（任意・後から編集できます）"
                onChange={(event) => setNewValue(event.target.value)}
                style={{ height: 34, border: "1px solid #cbd5e1", borderRadius: 8, padding: "0 10px", fontSize: 13 }}
              />
              {formError && <div style={{ fontSize: 12, color: "#dc2626" }}>{formError}</div>}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button variant="primary" disabled={createMut.isPending} onClick={handleCreate}>
                  {createMut.isPending ? "追加中..." : "作成して追加"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
